import type { components } from '@/data/api/schema';

type Schemas = components['schemas'];

export type User = Schemas['UserDto'];
export type AuthResponse = Schemas['AuthResponse'];
export type AuthInfo = Schemas['AuthInfoResponse'];

export type Platform = Schemas['PlatformDto'];
export type Service = Schemas['ServiceDto'];
export type Category = Schemas['CategoryDto'];
export type Order = Schemas['OrderDto'];
export type OrdersPage = Schemas['OrdersResponse'];
export type BuyResult = Schemas['BuyResponse'];

export type Home = Schemas['HomeResponse'];
export type DepositRecord = Schemas['DepositRecordDto'] & {
  id?: number;
  usdamount?: number;
  address?: string;
  expiresat?: number;
  receivedcoins?: number;
  status?: string;
  providerref?: string | null;
};
export type WithdrawRecord = Schemas['WithdrawRecordDto'] & {
  id?: number;
  fee?: number;
  coinscharged?: number;
  destinationaddress?: string | null;
  status?: string;
  providerref?: string | null;
  requestref?: string | null;
};
export type NewDepositResult = Schemas['NewDepositResponse'];
export type NewWithdrawResult = Schemas['NewWithdrawResponse'];
export type DepositState = Schemas['DepositStateResponse'];
export type NetworkOption = Schemas['NetworkOptionDto'];
export type WalletOptions = Schemas['OptionsDto'];
export type Poll = Schemas['PollResponse'];

export type ReferralOverview = Schemas['ReferralOverviewResponse'];
export type ReferralCollectResult = Schemas['ReferralCollectResponse'];

export type NewsItem = Schemas['NewsItemDto'];

export type WalletLinks = Schemas['WalletLinksResponse'];
export type ContactInfo = Schemas['ContactInfoResponse'];

export type DepositList = {
  records?: DepositRecord[];
  page?: number;
  pagesize?: number;
  total?: number;
};

export type WithdrawList = {
  records?: WithdrawRecord[];
  page?: number;
  pagesize?: number;
  total?: number;
};

export type AuthProvider = 'email' | 'google' | 'facebook' | 'x' | 'telegram';

// UI-friendly view of a service category. Maps the backend's free-text `category`
// to the icon/filter buckets used by the catalog screen.
export type ServiceKind = 'like' | 'follow' | 'view' | 'comment' | 'other';

export const classifyService = (s: Service): ServiceKind => {
  const c = (s.category ?? '').toLowerCase();
  if (c.includes('like')) return 'like';
  if (c.includes('follow') || c.includes('subscriber') || c.includes('member')) return 'follow';
  if (c.includes('view') || c.includes('impression')) return 'view';
  if (c.includes('comment') || c.includes('reply')) return 'comment';
  return 'other';
};

// Order total cost is derived from its frozen service reference snapshot.
export const orderTotalCost = (o: Order): number => {
  const ref = o.reference;
  const per = ref?.costper ?? 0;
  return Math.ceil((o.amount ?? 0) * per);
};

export const normalizeOrderState = (state?: string | null): string => {
  return String(state ?? '').replace(/[\s_-]+/g, '').toLowerCase();
};

export const orderState = (o: Order): 'active' | 'done' | 'cancelled' => {
  const state = normalizeOrderState(o.state);
  if (state === 'done' || state === 'finished' || state === 'completed' || state === 'complete' || state === 'success') {
    return 'done';
  }
  if (state === 'cancelled' || state === 'canceled' || state === 'failed') {
    return 'cancelled';
  }
  // Custom services often sit in review before an admin approves them.
  // Keep them in the active bucket so they do not disappear from the list.
  return 'active';
};
